
import React, { useState } from "react";
import emailjs from "emailjs-com";
import { motion } from "framer-motion";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    emailjs
      .sendForm("service_id", "template_id", e.target, "user_id")
      .then(
        () => alert("✅ Your message has been sent!"),
        () => alert("❌ Oops! Something went wrong, please try again.")
      );

    setFormData({ name: "", email: "", message: "" });
  };

  const sectionVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const inputVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="container mt-5 mb-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* 🌅 Hero Section */}
      <motion.section
        className="text-center"
        style={{
          background: "linear-gradient(120deg, #ffe7c2, #fff4e2, #fff9f3)",
          padding: "70px 20px",
          borderRadius: "20px",
          boxShadow: "0 8px 30px rgba(255,200,100,0.3)",
          color: "#5a3a0e",
          position: "relative",
          overflow: "hidden",
        }}
        variants={sectionVariant}
        initial="hidden"
        animate="visible"
      >
        {/* Glow effect */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            width: "300px",
            height: "300px",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 70%)",
            top: "-60px",
            left: "-60px",
            borderRadius: "50%",
            filter: "blur(60px)",
          }}
        />

        <h1
          className="display-4"
          style={{
            fontWeight: "800",
            fontSize: "3rem",
            textShadow: "0 2px 10px rgba(255,210,150,0.5)",
          }}
        >
          Contact Us
        </h1>
        <p
          className="lead"
          style={{
            fontSize: "1.25rem",
            marginTop: "20px",
            opacity: 0.9,
            lineHeight: "1.7",
            color: "#6b4a2e",
          }}
        >
          We’d love to hear from you.  
          Share your thoughts, questions, or feedback below — we’ll respond soon.
        </p>
      </motion.section>

      {/* 📩 Contact Form */}
      <motion.section
        className="mt-5 p-5"
        style={{
          background: "linear-gradient(to bottom, #fffaf3, #fff3e1)",
          borderRadius: "20px",
          boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
        }}
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <h2
          className="text-center mb-4"
          style={{
            color: "#b66b00",
            fontWeight: "700",
            fontSize: "2.3rem",
            textShadow: "0 2px 8px rgba(255,200,150,0.4)",
          }}
        >
          Get in Touch
        </h2>

        <form onSubmit={handleSubmit}>
          {["name", "email", "message"].map((field, index) => (
            <motion.div
              key={field}
              className="mb-4"
              variants={inputVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
            >
              <label
                htmlFor={field}
                className="form-label"
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "500",
                  color: "#5a3a0e",
                }}
              >
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </label>

              {field !== "message" ? (
                <motion.input
                  type={field === "email" ? "email" : "text"}
                  id={field}
                  name={field}
                  className="form-control"
                  value={formData[field]}
                  onChange={handleChange}
                  style={{
                    borderRadius: "10px",
                    fontSize: "1.05rem",
                    padding: "12px",
                    border: "1px solid rgba(255,200,150,0.5)",
                    backgroundColor: "#fffaf3",
                    color: "#4b2e05",
                  }}
                  whileFocus={{
                    scale: 1.02,
                    boxShadow: "0 0 12px rgba(255,190,120,0.6)",
                  }}
                  required
                />
              ) : (
                <motion.textarea
                  id={field}
                  name={field}
                  className="form-control"
                  rows="5"
                  value={formData[field]}
                  onChange={handleChange}
                  style={{
                    borderRadius: "10px",
                    fontSize: "1.05rem",
                    padding: "12px",
                    border: "1px solid rgba(255,200,150,0.5)",
                    backgroundColor: "#fffaf3",
                    color: "#4b2e05",
                  }}
                  whileFocus={{
                    scale: 1.02,
                    boxShadow: "0 0 12px rgba(255,190,120,0.6)",
                  }}
                  required
                ></motion.textarea>
              )}
            </motion.div>
          ))}

          <div className="text-center">
            <motion.button
              type="submit"
              className="btn btn-primary btn-lg"
              style={{
                background: "linear-gradient(145deg, #c97b2b, #ffb347)",
                border: "none",
                color: "#fff",
                padding: "12px 40px",
                borderRadius: "50px",
                fontSize: "1.1rem",
                cursor: "pointer",
                fontWeight: "600",
                boxShadow: "0 6px 16px rgba(255,190,120,0.5)",
              }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(255,200,150,0.6)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              Send Message
            </motion.button>
          </div>
        </form>
      </motion.section>

      {/* 📱 Responsive Styles */}
      <style>
        {`
          @media (max-width: 992px) {
            h1 { font-size: 2.2rem !important; }
            h2 { font-size: 2rem !important; }
            p, label { font-size: 1rem !important; }
            .p-5 { padding: 2rem 1.5rem !important; }
            input, textarea { font-size: 1rem !important; }
          }

          @media (max-width: 576px) {
            h1 { font-size: 1.8rem !important; line-height: 1.4; }
            h2 { font-size: 1.5rem !important; }
            p { font-size: 0.95rem !important; }
            button { width: 100%; font-size: 1rem !important; border-radius: 10px !important; }
            .p-5 { padding: 1.5rem 1rem !important; }
          }
        `}
      </style>
    </div>
  );
}

export default Contact;
